import React from "react";
//#region redux
import { AppState } from "store";
import { useSelector } from "react-redux";
//#endregion
//#region types
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { InvoiceFormTypes, UtilFormTypes } from "models/formTypes";
import { UploadedFile } from "models/utilTypes/fileUtilsTypes";
import { TranslationKeys } from "translations";
import { FormFieldConstants } from "utils/constants";
import { DimensionEnums } from "models/enums";
import { RutRotArticleItem } from "models/formTypes/invoice";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
import { ArraySchema, ObjectSchema } from "models/utilTypes/yupSchemaTypes";
//#endregion
//#region components
import { useDimensionMaps, usePeriodisationMinDate } from "components/common";
//#endregion
import * as Yup from "yup";
import { DateHelper, LocalizationHelperInstance, RegularExpressions } from "utils/helpers";

function useInvoiceFormValidations() {
  const { forcedDimensionMap } = useDimensionMaps(DimensionEnums.DimensionCategory.Invoice);
  const lockDate = useSelector((store: AppState) => store.auth.selectedCompany.lockDate);
  const hideDeliveryDate = useSelector((store: AppState) => store.auth.selectedCompany.hideInvoiceDeliveryDate);
  const periodisationMinDate = usePeriodisationMinDate();

  const isRotProperty = (
    propertyType: number,
    houseWorkType: number,
    type: WintSuperkollAPIModel.InvoiceRotPropertyType
  ) => {
    return houseWorkType === WintSuperkollAPIModel.InvoiceHouseWorkType.ROT && propertyType === type;
  };

  const transformProducts =
    (isDimensionAdvancedConfig: boolean, isPeriodisationAdvancedConfig: boolean) =>
    (value: InvoiceFormTypes.Product[]) => {
      return value.map((v, index) => {
        return {
          ...v,
          dimensions: !isDimensionAdvancedConfig && index > 0 ? value[0].dimensions : v.dimensions,
          periodisation: !isPeriodisationAdvancedConfig && index > 0 ? value[0].periodisation : v.periodisation,
          recurringPeriodisation:
            !isPeriodisationAdvancedConfig && index > 0 ? value[0].recurringPeriodisation : v.recurringPeriodisation,
        };
      });
    };

  const getProductFullSchema = (
    isSoftValidation?: boolean,
    hasReminderFee?: boolean,
    skipDimensionValidation?: boolean,
    isRecurring?: boolean
  ) => {
    return Yup.array<InvoiceFormTypes.Product>().when(
      [
        FormFieldConstants.Invoice.CreateBase.IsDimensionsAdvancedConfiguration,
        FormFieldConstants.Invoice.CreateBase.IsPeriodisationAdvancedConfiguration,
      ],
      (
        [isDimensionAdvancedConfig, isPeriodisationAdvancedConfig]: [boolean, boolean],
        schemaBeforeTransform: ArraySchema<InvoiceFormTypes.Product>
      ) => {
        const schema = schemaBeforeTransform.transform(
          transformProducts(isDimensionAdvancedConfig, isPeriodisationAdvancedConfig)
        );
        return isRecurring
          ? schema
              .required(TranslationKeys.Validations.AtLeastOneItem)
              .min(1, TranslationKeys.Validations.AtLeastOneItem)
              .of(
                Yup.object<InvoiceFormTypes.Product>({
                  ...getValidationSchemaProducts(skipDimensionValidation),
                  recurringPeriodisation: Yup.object<InvoiceFormTypes.RecurringPeriodisation>().shape(
                    {
                      from: Yup.number()
                        .min(-3)
                        .max(24)
                        .when(
                          FormFieldConstants.Invoice.RecurringPeriodisation.Duration,
                          ([duration], periodisationSchema: Yup.NumberSchema) =>
                            duration !== undefined
                              ? periodisationSchema.required(TranslationKeys.Validations.Required)
                              : periodisationSchema
                        ),
                      duration: Yup.number()
                        .min(1)
                        .when(
                          FormFieldConstants.Invoice.RecurringPeriodisation.From,
                          ([from], periodisationSchema: Yup.NumberSchema) =>
                            from !== undefined
                              ? periodisationSchema.required(TranslationKeys.Validations.Required)
                              : periodisationSchema
                        ),
                    },
                    [
                      [
                        FormFieldConstants.Invoice.RecurringPeriodisation.Duration,
                        FormFieldConstants.Invoice.RecurringPeriodisation.From,
                      ],
                    ]
                  ),
                })
              )
          : schema
              // eslint-disable-next-line no-empty-pattern
              .when([], ([], schema: ArraySchema<InvoiceFormTypes.Product>) => {
                return isSoftValidation || hasReminderFee
                  ? schema
                  : schema
                      .required(TranslationKeys.Validations.AtLeastOneItem)
                      .min(1, TranslationKeys.Validations.AtLeastOneItem);
              })
              .of(
                Yup.object<InvoiceFormTypes.Product>({
                  ...getValidationSchemaProducts(skipDimensionValidation, isSoftValidation),
                  periodisation: Yup.object<InvoiceFormTypes.InvoicePeriodisation>(
                    getValidationSchemaPeriodisation(isSoftValidation)
                  ),
                })
              );
      }
    );
  };

  const getValidationSchemaProducts = (skipDimensionValidation?: boolean, isSoftValidation?: boolean) => {
    const draftProductSchema = {
      id: Yup.number(),
      guid: Yup.string(),
      article: Yup.object<AutocompleteListItem>({
        id: Yup.number(),
        value: Yup.string(),
      }),
      price: Yup.number(),
      quantity: Yup.number(),
      unit: Yup.number(),
      tax: Yup.number(),
      totalAmountExTax: Yup.number(),
      taxText: Yup.string(),
      text: Yup.string(),
      periodisation: Yup.object<InvoiceFormTypes.InvoicePeriodisation>({
        isEnabled: Yup.boolean(),
        range: Yup.object<UtilFormTypes.DateRange>({
          start: Yup.date(),
          end: Yup.date(),
        }),
      }),
      recurringPeriodisation: Yup.object<InvoiceFormTypes.RecurringPeriodisation>({
        isEnabled: Yup.boolean(),
        from: Yup.number(),
        duration: Yup.number(),
      }),
      dimensions: skipDimensionValidation
        ? Yup.array().of(Yup.string())
        : Yup.array().of(Yup.string()).checkForcedDimensions(forcedDimensionMap),
      isEUCustomer: Yup.boolean(),
      eUVatCategoryId: Yup.number(),
    };

    if (isSoftValidation) return draftProductSchema;
    else
      return {
        ...draftProductSchema,
        price: draftProductSchema.price.required(TranslationKeys.Validations.Required),
        quantity: draftProductSchema.quantity.required(TranslationKeys.Validations.Required),
        tax: draftProductSchema.tax.required(TranslationKeys.Validations.Required),
        taxText: draftProductSchema.taxText.required(TranslationKeys.Validations.Required),
        eUVatCategoryId: draftProductSchema.eUVatCategoryId.when(
          FormFieldConstants.Invoice.Product.IsEUCustomer,
          ([isEUCompany], schema: Yup.NumberSchema) =>
            (isEUCompany &&
              schema
                .notOneOf(
                  [WintSuperkollAPIModel.EuVatProductType.NotDefined, WintSuperkollAPIModel.EuVatProductType.Unknown],
                  TranslationKeys.Validations.Required
                )
                .required(TranslationKeys.Validations.Required)) ||
            schema
        ),
      };
  };

  const getValidationSchemaPeriodisation = (isSoftValidation?: boolean) => {
    const periodisationRangeSchema = {
      start:
        periodisationMinDate !== undefined
          ? Yup.date().min(
              periodisationMinDate,
              LocalizationHelperInstance.getFormattedMessage(
                TranslationKeys.Validations.AccuralDateFallInPeriodisationPeriod,
                "",
                { lockDate: `${lockDate}` }
              )
            )
          : Yup.date(),
      end: Yup.date(),
    };

    const draftPeriodisationSchema = {
      isEnabled: Yup.boolean(),
      range: Yup.object<UtilFormTypes.DateRange>(periodisationRangeSchema),
    };

    if (isSoftValidation) return draftPeriodisationSchema;
    else
      return {
        ...draftPeriodisationSchema,
        range: draftPeriodisationSchema.range.validateMonthRange(TranslationKeys.Validations.Required),
      };
  };

  const getValidationSchemaSendEmail = (isSoftValidation?: boolean, skipEmailTextsRequired?: boolean) => {
    const softValidationSendMailSchema = {
      emailFrom: Yup.string(),
      emailFromType: Yup.number(),
      emailTo: Yup.array()
        .of(
          Yup.object<AutocompleteListItem>({
            id: Yup.string(),
            value: Yup.string().email(TranslationKeys.Validations.InvalidEmail),
          })
        )
        .compact((v: AutocompleteListItem) => !v || !v.value)
        .unique(TranslationKeys.Validations.EmailNotUnique, "value"),
      emailSubject: Yup.string(),
      emailMessage: Yup.string(),
    };

    if (isSoftValidation) return softValidationSendMailSchema;

    const sendMailBasicSchema = {
      ...softValidationSendMailSchema,
      emailFromType: softValidationSendMailSchema.emailFromType.required(TranslationKeys.Validations.Required),
      emailTo: softValidationSendMailSchema.emailTo
        .required(TranslationKeys.Validations.Required)
        .min(1, TranslationKeys.Validations.AtLeastOneItem),
    };

    if (skipEmailTextsRequired) return sendMailBasicSchema;

    return {
      ...sendMailBasicSchema,
      emailSubject: sendMailBasicSchema.emailSubject.required(TranslationKeys.Validations.Required),
      emailMessage: sendMailBasicSchema.emailMessage.required(TranslationKeys.Validations.Required),
    };
  };

  const getValidationSchemaRutRot = (isSoftValidation?: boolean) => {
    const draftSchema = {
      rutRotPersonNumber: Yup.string().when(
        FormFieldConstants.Invoice.RutRot.HouseWorkType,
        ([houseWorkType], scheme: Yup.StringSchema) =>
          houseWorkType
            ? scheme.matches(RegularExpressions.PersonNumber, {
                message: TranslationKeys.Validations.InvalidPersonNumber,
                excludeEmptyString: true,
              })
            : scheme
      ),
      rotPropertyType: Yup.number(),
      propertyDesignation: Yup.string(),
      tenantsOrgNumber: Yup.string().when(
        [FormFieldConstants.Invoice.RutRot.RotPropertyType, FormFieldConstants.Invoice.RutRot.HouseWorkType],
        ([propertyType, houseWorkType], scheme: Yup.StringSchema) =>
          isRotProperty(propertyType, houseWorkType, WintSuperkollAPIModel.InvoiceRotPropertyType.Condominium)
            ? scheme.matches(RegularExpressions.PersonNumber, TranslationKeys.Validations.OrgNumberInvalidLength)
            : scheme
      ),
      apartmentNumber: Yup.string(),
      houseWorkOtherCosts: Yup.number(),
      rutRotArticles: getRutRotArticleFullSchema(isSoftValidation),
    };

    if (isSoftValidation) return draftSchema;
    else
      return {
        ...draftSchema,
        rutRotPersonNumber: draftSchema.rutRotPersonNumber.when(
          FormFieldConstants.Invoice.RutRot.HouseWorkType,
          ([houseWorkType], scheme: Yup.StringSchema) =>
            houseWorkType ? scheme.required(TranslationKeys.Validations.Required) : scheme
        ),
        rotPropertyType: draftSchema.rotPropertyType.when(
          FormFieldConstants.Invoice.RutRot.HouseWorkType,
          ([houseWorkType], scheme: Yup.NumberSchema) =>
            houseWorkType === WintSuperkollAPIModel.InvoiceHouseWorkType.ROT
              ? scheme.required(TranslationKeys.Validations.Required)
              : scheme
        ),
        propertyDesignation: draftSchema.propertyDesignation.when(
          [FormFieldConstants.Invoice.RutRot.RotPropertyType, FormFieldConstants.Invoice.RutRot.HouseWorkType],
          ([propertyType, houseWorkType], scheme: Yup.StringSchema) =>
            isRotProperty(propertyType, houseWorkType, WintSuperkollAPIModel.InvoiceRotPropertyType.House)
              ? scheme.required(TranslationKeys.Validations.Required)
              : scheme
        ),
        tenantsOrgNumber: draftSchema.tenantsOrgNumber.when(
          [FormFieldConstants.Invoice.RutRot.RotPropertyType, FormFieldConstants.Invoice.RutRot.HouseWorkType],
          ([propertyType, houseWorkType], scheme: Yup.StringSchema) =>
            isRotProperty(propertyType, houseWorkType, WintSuperkollAPIModel.InvoiceRotPropertyType.Condominium)
              ? scheme.required(TranslationKeys.Validations.Required)
              : scheme
        ),
        apartmentNumber: draftSchema.apartmentNumber.when(
          [FormFieldConstants.Invoice.RutRot.RotPropertyType, FormFieldConstants.Invoice.RutRot.HouseWorkType],
          ([propertyType, houseWorkType], scheme: Yup.StringSchema) =>
            isRotProperty(propertyType, houseWorkType, WintSuperkollAPIModel.InvoiceRotPropertyType.Condominium)
              ? scheme.required(TranslationKeys.Validations.Required)
              : scheme
        ),
        houseWorkOtherCosts: draftSchema.houseWorkOtherCosts.when(
          FormFieldConstants.Invoice.RutRot.HouseWorkType,
          ([houseWorkType], scheme: Yup.NumberSchema) =>
            houseWorkType ? scheme.required(TranslationKeys.Validations.Required) : scheme
        ),
      };
  };

  const getRutRotArticleFullSchema = (isSoftValidation?: boolean) => {
    return Yup.array<InvoiceFormTypes.RutRotArticleItem>().when(
      FormFieldConstants.Invoice.RutRot.HouseWorkType,
      ([houseWorkType]: [number], scheme: ArraySchema<InvoiceFormTypes.RutRotArticleItem>) =>
        houseWorkType
          ? (isSoftValidation
              ? scheme
              : scheme
                  .required(TranslationKeys.Validations.AtLeastOneItem)
                  .min(1, TranslationKeys.Validations.AtLeastOneItem)
            ).of(Yup.object<RutRotArticleItem>(getValidationSchemaRutRotArticle(isSoftValidation)))
          : scheme
    );
  };

  const getValidationSchemaRutRotArticle = (isSoftValidation?: boolean) => {
    const rutRotArticleItemSoftSchema = {
      id: Yup.number(),
      name: Yup.string(),
      typeOfWorkId: Yup.number(),
      noOfHours: Yup.number()
        .min(0, TranslationKeys.Invoice.RutRotHoursValidation)
        .max(1000, TranslationKeys.Invoice.RutRotHoursValidation),
      materialCost: Yup.number(),
      materialCostSuggestionsId: Yup.number(),
    };

    if (isSoftValidation) return rutRotArticleItemSoftSchema;

    return {
      ...rutRotArticleItemSoftSchema,
      id: rutRotArticleItemSoftSchema.id.required(TranslationKeys.Validations.Required),
      typeOfWorkId: rutRotArticleItemSoftSchema.typeOfWorkId.required(TranslationKeys.Validations.Required),
      noOfHours: rutRotArticleItemSoftSchema.noOfHours.required(TranslationKeys.Validations.Required),
      materialCost: rutRotArticleItemSoftSchema.materialCost.required(TranslationKeys.Validations.Required),
    };
  };

  const getValidationSchemaOtherSettings = (
    isQuotation?: boolean,
    isSoftValidation?: boolean,
    isRecurring?: boolean
  ) => {
    const getPenaltyInterestPercentageSchema = () =>
      Yup.number().when(
        [FormFieldConstants.Invoice.OtherSettings.PenaltyInterestType],
        ([penaltyInterestType], schema: Yup.NumberSchema) => {
          const draftSchema =
            !isRecurring && penaltyInterestType === WintSuperkollAPIModel.InvoicePenaltyInterestType.Prearranged
              ? schema.max(
                  100,
                  LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Validations.Max, "", { max: "100" })
                )
              : schema;

          return isSoftValidation
            ? draftSchema
            : !isRecurring && penaltyInterestType === WintSuperkollAPIModel.InvoicePenaltyInterestType.Prearranged
              ? draftSchema.required(TranslationKeys.Validations.Required)
              : draftSchema;
        }
      );

    const getAutomaticReminderPeriodSchema = () =>
      Yup.number().when(
        [FormFieldConstants.Invoice.OtherSettings.IsAutomaticRemindersEnabled],
        ([isAutomaticRemindersEnabled], schema: Yup.NumberSchema) => {
          const draftSchema =
            !isRecurring && isAutomaticRemindersEnabled
              ? schema.when(
                  FormFieldConstants.Invoice.OtherSettings.AutomaticReminderDelay,
                  ([automaticReminderDelay], schema: Yup.NumberSchema) => {
                    const minValue = automaticReminderDelay ? Math.max(5, automaticReminderDelay + 1) : 5;
                    return schema.min(
                      minValue,
                      LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Validations.Min, "", {
                        min: `${minValue}`,
                      })
                    );
                  }
                )
              : schema;
          return isSoftValidation
            ? draftSchema
            : !isRecurring && isAutomaticRemindersEnabled
              ? draftSchema.required(TranslationKeys.Validations.Required)
              : draftSchema;
        }
      );

    const getAutomaticReminderSendTypeSchema = () =>
      Yup.number()
        .default(undefined)
        .when(
          [FormFieldConstants.Invoice.OtherSettings.IsAutomaticRemindersEnabled],
          ([isAutomaticRemindersEnabled], schema: Yup.NumberSchema) =>
            !isSoftValidation && !isRecurring && isAutomaticRemindersEnabled
              ? schema.required(TranslationKeys.Validations.Required)
              : schema
        );

    const draftSchema: Record<keyof InvoiceFormTypes.OtherSettings, Yup.Schema> = {
      invoiceDate: !isRecurring
        ? Yup.date().required(TranslationKeys.Validations.Required).typeError(TranslationKeys.Validations.InvalidDate)
        : Yup.date(),
      deliveryDate:
        isRecurring || hideDeliveryDate
          ? Yup.date()
          : Yup.date()
              .typeError(TranslationKeys.Validations.InvalidDate)
              .required(TranslationKeys.Validations.Required),
      dueDate: !isRecurring
        ? Yup.date()
            .min(
              Yup.ref(FormFieldConstants.Invoice.OtherSettings.InvoiceDate),
              TranslationKeys.Validations.InvoiceDueDateGreaterThanInvoiceDate
            )
            .required(TranslationKeys.Validations.Required)
            .typeError(TranslationKeys.Validations.InvalidDate)
        : Yup.date(),
      notes: Yup.string(),
      paymentTerms: Yup.number()
        .typeError(TranslationKeys.Validations.InvalidPaymentTerms)
        .min(0, TranslationKeys.Validations.InvalidPaymentTerms),
      languageKey: Yup.string(),
      invoiceLayout: Yup.number(),
      showCurrencyCode: Yup.boolean(),
      showCustomerDeliveryAddress: Yup.boolean(),
      showReferenceInAddress: Yup.boolean(),
      paymentMethodId: Yup.number(),
      sellerName: Yup.string(),
      attachments: Yup.array().of(
        Yup.object<UploadedFile>({
          contentType: Yup.string(),
          fileName: Yup.string(),
          fileSize: Yup.number(),
          data: Yup.string(),
        })
      ),
      reminderCost: Yup.number(),
      penaltyInterestType: Yup.number(),
      penaltyInterestPercentage: getPenaltyInterestPercentageSchema(),
      isAutomaticRemindersEnabled: Yup.boolean(),
      automaticReminderDelay: Yup.number(),
      automaticReminderAmountLimit: Yup.number(),
      automaticReminderPeriod: getAutomaticReminderPeriodSchema(),
      automaticReminderCountLimit: Yup.number(),
      automaticReminderSendType: getAutomaticReminderSendTypeSchema(),
      automaticReminderSendMail: Yup.object().when(
        [
          FormFieldConstants.Invoice.OtherSettings.IsAutomaticRemindersEnabled,
          FormFieldConstants.Invoice.OtherSettings.AutomaticReminderSendType,
        ],
        ([isAutomaticRemindersEnabled, sendType]) =>
          !isRecurring && isAutomaticRemindersEnabled && sendType === WintSuperkollAPIModel.InvoiceSendMethod.Email
            ? Yup.object<InvoiceFormTypes.SendMail>(getValidationSchemaSendEmail(isSoftValidation))
            : Yup.object()
      ),
      automaticReminderNotes: Yup.string(),
      defaultReminderEmailToSettings: Yup.object<InvoiceFormTypes.DefaultEmailToSettings>(),
    };

    if (isSoftValidation) return Yup.object<InvoiceFormTypes.OtherSettings>(draftSchema);
    else {
      return Yup.object<InvoiceFormTypes.OtherSettings>({
        ...draftSchema,
        languageKey: draftSchema.languageKey.required(TranslationKeys.Validations.Required),
        paymentMethodId: !isQuotation
          ? draftSchema.paymentMethodId.required(TranslationKeys.Validations.Required)
          : draftSchema.paymentMethodId,
        penaltyInterestType:
          !isQuotation && !isRecurring
            ? draftSchema.penaltyInterestType.required(TranslationKeys.Validations.Required)
            : draftSchema.penaltyInterestType,
      });
    }
  };

  const getValidationSchemaRecurringSettings = (
    isRecurringInvoiceActive?: boolean,
    isSoftValidation?: boolean,
    isRecurring?: boolean
  ): Record<keyof InvoiceFormTypes.RecurringSettings, Yup.AnySchema> => {
    const draftSchema = {
      recurringDescription: Yup.string(),
      recurringOwner: Yup.object<AutocompleteListItem>(getValidationSchemaSelectionItems()).default(undefined),
      recurringInvoiceSendType: Yup.number(),
      recurringInvoiceAutomaticSendType: Yup.number(),
      recurringSendMail: Yup.object().when(
        [FormFieldConstants.Invoice.RecurringSettings.RecurringInvoiceAutomaticSendType],
        ([automaticSendType], schema: ObjectSchema<InvoiceFormTypes.SendMail>) =>
          isRecurring && automaticSendType === WintSuperkollAPIModel.InvoiceSendType.SendByEmail
            ? Yup.object<InvoiceFormTypes.SendMail>(getValidationSchemaSendEmail(isSoftValidation, true))
            : schema
      ),
      recurringInvoiceStartDate:
        isRecurring && isRecurringInvoiceActive
          ? Yup.date()
              .typeError(TranslationKeys.Validations.InvalidDate)
              .min(
                DateHelper.resetTime(DateHelper.addDays(new Date(), 1)) as Date,
                TranslationKeys.Validations.ShouldBeFutureDate
              )
          : Yup.date(),
      recurringInvoiceFrequency: Yup.number(),
      recurringRepeatUntilNotice: Yup.boolean(),
      recurringInvoiceEndDate: Yup.date().when(
        [
          FormFieldConstants.Invoice.RecurringSettings.RecurringRepeatUntilNotice,
          FormFieldConstants.Invoice.RecurringSettings.RecurringInvoiceStartDate,
        ],
        ([repeatUntilNotice, startDate], schema: Yup.DateSchema) =>
          isRecurring && !repeatUntilNotice
            ? schema
                .typeError(TranslationKeys.Validations.InvalidDate)
                .min(
                  DateHelper.addDays(startDate && !isNaN(Date.parse(startDate.toString())) ? startDate : new Date(), 1),
                  TranslationKeys.Validations.InvoiceStartDateGreaterThanStopDate
                )
            : schema
      ),
      recurringDeliveryAfterDays: Yup.number(),
    };

    if (isSoftValidation) return draftSchema;
    else
      return {
        ...draftSchema,
        recurringDescription: isRecurring
          ? draftSchema.recurringDescription.required(TranslationKeys.Validations.Required)
          : draftSchema.recurringDescription,
        recurringOwner: isRecurring
          ? draftSchema.recurringOwner.required(TranslationKeys.Validations.Required)
          : draftSchema.recurringOwner,
        recurringInvoiceSendType: isRecurring
          ? draftSchema.recurringInvoiceSendType.required(TranslationKeys.Validations.Required)
          : draftSchema.recurringInvoiceSendType,
        recurringInvoiceAutomaticSendType: draftSchema.recurringInvoiceAutomaticSendType.when(
          [FormFieldConstants.Invoice.RecurringSettings.RecurringInvoiceSendType],
          ([sendType], schema: Yup.NumberSchema) =>
            isRecurring && sendType === WintSuperkollAPIModel.RecurringInvoiceSendType.Auto
              ? schema.required(TranslationKeys.Validations.Required)
              : schema
        ),
        recurringInvoiceStartDate:
          isRecurring && isRecurringInvoiceActive
            ? draftSchema.recurringInvoiceStartDate.required(TranslationKeys.Validations.Required)
            : draftSchema.recurringInvoiceStartDate,
        recurringInvoiceFrequency: isRecurring
          ? draftSchema.recurringInvoiceFrequency.required(TranslationKeys.Validations.Required)
          : draftSchema.recurringInvoiceFrequency,
        recurringInvoiceEndDate: draftSchema.recurringInvoiceEndDate.when(
          [FormFieldConstants.Invoice.RecurringSettings.RecurringRepeatUntilNotice],
          ([repeatUntilNotice], schema: Yup.DateSchema) =>
            isRecurring && !repeatUntilNotice ? schema.required(TranslationKeys.Validations.Required) : schema
        ),
      };
  };

  const getValidationSchemaSelectionItems = () => {
    return {
      id: Yup.string() || Yup.number(),
      value: Yup.string(),
      description: Yup.string(),
      icon: Yup.object(),
      avatar: Yup.object(),
      selectedSubText: Yup.string(),
      selectionTileProps: Yup.object(),
      selected: Yup.boolean(),
    };
  };

  const getValidationSchemaInvoiceBasicProps = (
    isSoftValidation?: boolean
  ): Record<keyof InvoiceFormTypes.Create, Yup.AnySchema> => {
    const selectionItemSchema = getValidationSchemaSelectionItems();

    const softValidationInvoiceSchema: Record<keyof InvoiceFormTypes.Create, Yup.AnySchema> = {
      id: Yup.number(),
      serialNumber: Yup.number(),
      quotationSerialNumber: Yup.number(),
      ownerId: Yup.number(),
      loadingCustomer: Yup.number().nullable().oneOf([null], TranslationKeys.Validations.Required),
      customer: Yup.object<AutocompleteListItem>(selectionItemSchema)
        .default(undefined)
        .required(TranslationKeys.Validations.Required),
      customerRating: Yup.number(),
      referencePerson: Yup.object<AutocompleteListItem>(selectionItemSchema),
      isEUCustomer: Yup.boolean(),
      isDomesticCustomer: Yup.boolean(),
      total: Yup.number(),
      tax: Yup.number(),
      currency: Yup.string(),
      isDimensionsAdvancedConfiguration: Yup.boolean(),
      isPeriodisationAdvancedConfiguration: Yup.boolean(),
      isPeriodisationByDate: Yup.boolean(),
      fromRecurring: Yup.boolean(),
      products: Yup.array<InvoiceFormTypes.Product>(),
      rutRot: Yup.object(),
      otherSettings: Yup.object<InvoiceFormTypes.OtherSettings>(),
      recurringSettings: Yup.object<InvoiceFormTypes.RecurringSettings>(),
      mailOptions: Yup.object(),
      previewSendMail: Yup.object(),
      defaultSendType: Yup.number(),
      defaultEmailToSettings: Yup.object(),
      forwardedCosts: Yup.array<InvoiceFormTypes.ForwardedCosts>(),
    };

    if (isSoftValidation) {
      return softValidationInvoiceSchema;
    }

    const fullSchema = {
      ...softValidationInvoiceSchema,
      currency: softValidationInvoiceSchema.currency.required(TranslationKeys.Validations.Required),
    };

    return fullSchema;
  };

  const getInvoiceValidationProperties = (
    isRecurringInvoiceActive?: boolean,
    isQuotation?: boolean,
    isSoftValidation?: boolean,
    hasReminderFee?: boolean,
    isCreditInvoice?: boolean,
    isRecurring?: boolean
  ) => {
    const invoiceBasicValidationSchema = getValidationSchemaInvoiceBasicProps(isQuotation);

    return {
      ...invoiceBasicValidationSchema,
      products: getProductFullSchema(isSoftValidation, hasReminderFee, isCreditInvoice, isRecurring),
      rutRot: Yup.object<InvoiceFormTypes.RutRot>(getValidationSchemaRutRot(isSoftValidation)),
      otherSettings: getValidationSchemaOtherSettings(isQuotation, isSoftValidation, isRecurring),
      recurringSettings: Yup.object<InvoiceFormTypes.RecurringSettings>(
        getValidationSchemaRecurringSettings(isRecurringInvoiceActive, isSoftValidation, isRecurring)
      ),
    };
  };

  const getValidationSchema = React.useCallback(
    (
      isRecurringInvoiceActive?: boolean,
      isQuotation?: boolean,
      isSoftValidation?: boolean,
      hasReminderFee?: boolean,
      isCreditInvoice?: boolean,
      isRecurring?: boolean
    ) => {
      return Yup.object<InvoiceFormTypes.Create>(
        getInvoiceValidationProperties(
          isRecurringInvoiceActive,
          isQuotation,
          isSoftValidation,
          hasReminderFee,
          isCreditInvoice,
          isRecurring ?? isRecurringInvoiceActive
        )
      );
    },
    [forcedDimensionMap]
  );

  return {
    getValidationSchema,
    getInvoiceValidationProperties,
    getValidationSchemaSendEmail,
    getProductFullSchema,
    getValidationSchemaProducts,
  };
}

export default useInvoiceFormValidations;
